import { GroupedQuoteType, getGroupedQuoteType, ICoverage, IQuote, Coverage, ResidentialCoverage } from '@/interfaces';
import { BuildingType } from '@/enums/building-types-enum';
import { convertISOToDate } from '../data-transforms';
import { equalsString } from '../check-helpers';
import { CarPurchaseAdditionalPersonType } from '@/enums/car-purchase-addl-person-type';
import { SubmitQuoteResponse } from '@/services/store/insurance-quote.service';

// Function to get insurance policy display name
export function getInsurancePolicyDisplayName(dictionary: Record<string, string>, coverageCode: string): string {
  if (!dictionary || !coverageCode) {
    return '';
  }
  return dictionary[coverageCode] || coverageCode;
}

// Function to get coverage descriptions by coverage code
export function getCoverageDescriptionsByCoverageCode(coverage: ICoverage | null | undefined): string {
  const coverageCode = coverage?.coverageCode;
  if (!coverage || !coverageCode) {
    return '';
  }

  const groupedQuoteType = getGroupedQuoteType(coverage.coverageCode);

  switch (groupedQuoteType) {
    case GroupedQuoteType.Motor:
    case GroupedQuoteType.Motorcycle:
      return `${coverage.vehicle?.year ?? ''} ${coverage.vehicle?.make ?? ''} ${coverage.vehicle?.model ?? ''}`.trim();
    case GroupedQuoteType.Home:
      return coverage.riskAddress?.formattedStreet ?? '';
    case GroupedQuoteType.Pet:
      return coverage.petName ?? '';
    case GroupedQuoteType.Caravan:
    case GroupedQuoteType.Boat:
      return coverage.vehicle?.formattedSummary ?? '';
    default:
      return coverage.vehicle?.formattedSummary ?? coverage.riskAddress?.formattedStreet ?? '';
  }
}

export function isDuplicatePolicyHolder(
  quote: IQuote,
  firstName: string,
  lastName: string,
  dateOfBirth: any,
  stepUuid: string,
  policyholderOrDriver?: string
): boolean {
  const isPolicyholderCheckRequired =
    !policyholderOrDriver ||
    [CarPurchaseAdditionalPersonType.POLICYHOLDER, CarPurchaseAdditionalPersonType.BOTH].includes(
      policyholderOrDriver as CarPurchaseAdditionalPersonType
    );

  if (!isPolicyholderCheckRequired) return false;

  const policyholderDOB = quote.primaryPolicyHolderDetails?.dateOfBirth?.value;
  const driverDOB = quote.motorQuote?.primaryDriverDetails?.driverDateOfBirth?.value;

  let mainDOB = '';

  if (policyholderDOB) {
    mainDOB = convertISOToDate(policyholderDOB.toISOString?.() ?? policyholderDOB);
  } else if (driverDOB) {
    mainDOB = convertISOToDate(driverDOB.toISOString?.() ?? driverDOB);
  }

  const isPrimaryDuplicate =
    equalsString(quote.primaryPolicyHolderDetails?.firstname?.value, firstName) &&
    equalsString(quote.primaryPolicyHolderDetails?.surname?.value, lastName) &&
    mainDOB === dateOfBirth;

  if (isPrimaryDuplicate) return true;

  // Check for duplicates in additional policy holders
  return (
    quote.additionalPolicyHolderDetails
      ?.filter((holder) => holder.policyHolderId !== stepUuid)
      .some((holder) => {
        const holderDOB = holder.dateOfBirth?.value
          ? convertISOToDate(holder.dateOfBirth.value.toISOString?.() ?? holder.dateOfBirth.value)
          : '';

        return (
          equalsString(holder.firstname?.value, firstName) &&
          equalsString(holder.surname?.value, lastName) &&
          holderDOB === dateOfBirth
        );
      }) ?? false
  );
}

export function quoteComplete(response: SubmitQuoteResponse) {
  return response?.success || (response?.quote?.policyReference?.length ?? 0) > 0;
}

export function hasMetFloodAndCycloneMitigationMinimumRequirements(
  coverage: Coverage,
  isCycloneCovered?: boolean
): boolean {
  let hasMetFloodMinimumReqs = false;
  let hasMetCycloneMinimumReqs = true;
  const residential = coverage as ResidentialCoverage;

  // set the broader logic
  switch (residential.propertyType) {
    case BuildingType[BuildingType.House]:
    case BuildingType[BuildingType.Townhouse]:
    case BuildingType[BuildingType.Duplex]:
    case BuildingType[BuildingType.BoardingHome]:
    case BuildingType[BuildingType.ManufTransportableHome]:
    case BuildingType[BuildingType.Storage]:
      hasMetFloodMinimumReqs =
        residential.propertyElevated !== null &&
        residential.propertyElevated !== undefined &&
        residential.numOfStoreys !== null &&
        residential.numOfStoreys !== undefined;
      break;
    case BuildingType[BuildingType.Unit]:
    case BuildingType[BuildingType.Flat]:
      hasMetFloodMinimumReqs = residential.occupiedFloor !== null && residential.occupiedFloor !== undefined;
      break;
    default:
      break;
  }

  // check for cyclone data requirements
  if (
    isCycloneCovered === true &&
    residential.propertyType !== BuildingType[BuildingType.Storage] &&
    residential.propertyType !== BuildingType[BuildingType.ManufTransportableHome] &&
    residential.propertyType !== BuildingType[BuildingType.BoardingHome]
  ) {
    hasMetCycloneMinimumReqs = residential.windowProtection !== null && residential.windowProtection !== undefined;
  }

  return hasMetFloodMinimumReqs && hasMetCycloneMinimumReqs;
}
